import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useDispatch } from "react-redux";
import { Grid } from "@mui/material";

import BasicModal from "examples/modal/BasicModal/BasicModal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Status from "components/Table/Status";

import Constants, { ButtonTitles, Icons, ModalContent, defaultData } from "utils/Constants";
import { getFormattedCallingFirstName, getFormattedProjectName } from "utils/methods/methods";
import { getSnagById } from "redux/Thunks/Snag";
import { openSnackbar } from "redux/Slice/Notification";

import { formatSnagDate, formatSnagStatus } from "./data/SnagData";

function DetailItem({ label, children }) {
  return (
    <MDBox>
      <MDTypography variant="caption" sx={{ color: "#667085", fontWeight: 500 }}>
        {label}
      </MDTypography>
      <MDBox mt={0.5}>{children}</MDBox>
    </MDBox>
  );
}

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

DetailItem.defaultProps = {
  children: null,
};

function SnagViewModal({ open, handleClose, snag, snagId }) {
  const dispatch = useDispatch();
  const [snagDetails, setSnagDetails] = useState(snag);
  const [loading, setLoading] = useState(false);
  // Latest close handler without re-running the fetch effect (callers pass inline functions)
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  useEffect(() => {
    let isActive = true;
    setSnagDetails(snag);

    if (open && snagId) {
      setLoading(true);
      dispatch(getSnagById(snagId)).then((res) => {
        if (!isActive) return;
        if (res?.payload?.status === 200) {
          setSnagDetails(res.payload.data?.data || snag);
        } else {
          // e.g. Snag access removed after login - show why instead of an empty modal
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
              notificationType: Constants.NOTIFICATION_ERROR,
            })
          );
          handleCloseRef.current();
        }
        setLoading(false);
      });
    }

    return () => {
      isActive = false;
    };
  }, [dispatch, open, snag, snagId]);

  const textStyle = { color: "#111827", fontWeight: 500, textTransform: "none" };

  return (
    <BasicModal
      title={ModalContent.VIEW_SNAG_TITLE}
      open={open}
      handleClose={handleClose}
      actionButton={ButtonTitles.CLOSE}
      handleAction={handleClose}
      displayButtons={false}
      minWidth={600}
      py={3}
    >
      {loading && !snagDetails ? (
        <MDBox display="flex" justifyContent="center" py={3}>
          {Icons.LOADING2}
        </MDBox>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <DetailItem label="Project">
              <MDTypography variant="button" sx={textStyle}>
                {snagDetails?.project ? getFormattedProjectName(snagDetails.project) : Constants.NA}
              </MDTypography>
            </DetailItem>
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem label="Location">
              <MDTypography variant="button" sx={textStyle}>
                {snagDetails?.location?.title || Constants.NA}
              </MDTypography>
            </DetailItem>
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem label="Date Created">
              <MDTypography variant="button" sx={textStyle}>
                {formatSnagDate(snagDetails?.dateAdded)}
              </MDTypography>
            </DetailItem>
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem label="Status">
              <Status title={formatSnagStatus(snagDetails?.status)} />
            </DetailItem>
          </Grid>
          <Grid item xs={12}>
            <DetailItem label="Details">
              <MDTypography
                variant="button"
                sx={{ ...textStyle, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {snagDetails?.details || Constants.NA}
              </MDTypography>
            </DetailItem>
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem label="Created By">
              <MDTypography variant="button" sx={textStyle}>
                {snagDetails?.createdBy
                  ? `${getFormattedCallingFirstName(snagDetails.createdBy)} (${moment(
                      snagDetails.createdAt
                    ).format(defaultData.WEB_DATE_FORMAT)})`
                  : Constants.NA}
              </MDTypography>
            </DetailItem>
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem label="Last Changed By">
              <MDTypography variant="button" sx={textStyle}>
                {snagDetails?.updatedBy
                  ? `${getFormattedCallingFirstName(snagDetails.updatedBy)} (${moment(
                      snagDetails.updatedAt
                    ).format(defaultData.WEB_DATE_FORMAT)})`
                  : Constants.NA}
              </MDTypography>
            </DetailItem>
          </Grid>
        </Grid>
      )}
    </BasicModal>
  );
}

SnagViewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  snag: PropTypes.objectOf(PropTypes.any),
  snagId: PropTypes.string,
};

SnagViewModal.defaultProps = {
  snag: null,
  snagId: "",
};

export default SnagViewModal;
